import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Home, Plus, PlayCircle, StopCircle, RefreshCw, UserPlus, Shield, BarChart3, Users } from "lucide-react";

interface AssessmentRun {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  created_at: string;
}

interface UserAttempt {
  id: string;
  user_id: string;
  run_id: string;
  completed: boolean;
  overall_score: number | null;
  started_at: string;
  completed_at: string | null;
  profiles: {
    first_name: string;
    last_name: string;
  } | null;
  assessment_runs: {
    name: string;
  } | null;
}

interface User {
  id: string;
  email: string;
  profiles: {
    first_name: string;
    last_name: string;
  } | null;
  user_roles: { role: string }[];
}

const AdminDashboard = () => {
  const { isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [runs, setRuns] = useState<AssessmentRun[]>([]);
  const [attempts, setAttempts] = useState<UserAttempt[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [newRun, setNewRun] = useState({ name: "", description: "" });
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedRunId, setSelectedRunId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoading && !isAdmin) {
      toast({
        title: "Access Denied",
        description: "You don't have admin privileges",
        variant: "destructive",
      });
      navigate("/");
    }
  }, [isAdmin, isLoading, navigate, toast]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([fetchRuns(), fetchAttempts(), fetchUsers()]);
    setLoading(false);
  };

  const fetchRuns = async () => {
    const { data } = await supabase
      .from("assessment_runs")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setRuns(data);
  };

  const fetchAttempts = async () => {
    const { data } = await supabase
      .from("attempts")
      .select("*")
      .order("started_at", { ascending: false });

    if (data) {
      const attemptsWithProfiles = await Promise.all(
        data.map(async (attempt) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("first_name, last_name")
            .eq("id", attempt.user_id)
            .single();

          const { data: run } = await supabase
            .from("assessment_runs")
            .select("name")
            .eq("id", attempt.run_id)
            .single();

          return {
            ...attempt,
            profiles: profile,
            assessment_runs: run,
          };
        })
      );
      setAttempts(attemptsWithProfiles);
    }
  };

  const fetchUsers = async () => {
    const { data } = await supabase
      .from("profiles")
      .select("id, first_name, last_name");

    if (data) {
      const usersWithDetails = await Promise.all(
        data.map(async (profile) => {
          const { data: authUser } = await supabase.auth.admin.getUserById(profile.id);

          const { data: roles } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", profile.id);

          return {
            id: profile.id,
            email: authUser?.user?.email || "N/A",
            profiles: { first_name: profile.first_name, last_name: profile.last_name },
            user_roles: roles || [],
          };
        })
      );
      setUsers(usersWithDetails);
    }
  };

  const createRun = async () => {
    if (!newRun.name.trim()) {
      toast({ title: "Error", description: "Run name is required", variant: "destructive" });
      return;
    }

    const { error } = await supabase.from("assessment_runs").insert({
      name: newRun.name,
      description: newRun.description,
      is_active: false,
    });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Assessment run created" });
      setNewRun({ name: "", description: "" });
      fetchRuns();
    }
  };

  const toggleRunStatus = async (runId: string, currentStatus: boolean) => {
    const { error } = await supabase
      .from("assessment_runs")
      .update({ is_active: !currentStatus })
      .eq("id", runId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({
        title: "Success",
        description: `Run ${!currentStatus ? "activated" : "deactivated"}`
      });
      fetchRuns();
    }
  };

  const resetAssessment = async () => {
    if (!selectedUserId || !selectedRunId) {
      toast({ title: "Error", description: "Select user and run", variant: "destructive" });
      return;
    }

    try {
      // Use the Nginx proxy path to avoid CORS/Port issues
      const response = await fetch("/auth/ldap/admin/reset-assessment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: selectedUserId,
          runId: selectedRunId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast({ title: "Success", description: "Assessment reset successfully. User can now start fresh." });
        fetchAttempts();
        setSelectedUserId("");
        setSelectedRunId("");
      } else {
        throw new Error(data.message || "Failed to reset assessment");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to connect to server",
        variant: "destructive",
      });
    }
  };

  const toggleAdminRole = async (userId: string, isCurrentlyAdmin: boolean) => {
    if (isCurrentlyAdmin) {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", "admin");

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Success", description: "Admin role removed" });
        fetchUsers();
      }
    } else {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: "admin" });

      if (error) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Success", description: "Admin role granted" });
        fetchUsers();
      }
    }
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Bilingual Instructions */}
        <Card className="p-8 mb-6 bg-gradient-to-r from-primary/5 to-accent/5 border-primary/30 shadow-lg">
          <div className="space-y-3">
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              <strong className="text-primary text-xl md:text-2xl">Instructions:</strong> Manage assessment runs, view all user attempts, reopen surveys for individual users, and manage admin roles. Navigate to "All Participants" to view detailed responses.
            </p>
            <p className="text-lg md:text-xl font-semibold text-foreground font-tajawal leading-relaxed" dir="rtl">
              <strong className="text-primary text-xl md:text-2xl">التعليمات:</strong> إدارة جولات التقيي م، عرض جميع محاولات المستخدمين، إعادة فتح الاستبيانات للمستخدمين الأفراد، وإدارة أدوار المشرفين. انتقل إلى "جميع المشاركين" لعرض الإجابات التفصيلية.
            </p>
          </div>
        </Card>

        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-foreground">Admin Dashboard</h1>
          <div className="flex gap-3">
            <Button onClick={() => navigate("/admin/analytics")} className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Analytics
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate("/admin/participants")} className="gap-2">
                <Users className="h-4 w-4" />
                All Participants
              </Button>
              <Button variant="outline" onClick={() => navigate("/")} className="gap-2">
                <Home className="h-4 w-4" />
                Home
              </Button>
            </div>
          </div>
        </div>

        {/* Assessment Runs Section */}
        <Card className="p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-foreground">Assessment Runs</h2>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Run
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Assessment Run</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name">Run Name</Label>
                    <Input
                      id="name"
                      value={newRun.name}
                      onChange={(e) => setNewRun({ ...newRun, name: e.target.value })}
                      placeholder="e.g., Fall 2024"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={newRun.description}
                      onChange={(e) => setNewRun({ ...newRun, description: e.target.value })}
                      placeholder="Optional description"
                    />
                  </div>
                  <Button onClick={createRun} className="w-full">Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell className="font-medium">{run.name}</TableCell>
                  <TableCell>{run.description || "-"}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${run.is_active ? "bg-success/20 text-success" : "bg-muted text-muted-foreground"
                      }`}>
                      {run.is_active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell>{new Date(run.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant={run.is_active ? "destructive" : "default"}
                      onClick={() => toggleRunStatus(run.id, run.is_active)}
                      className="gap-2"
                    >
                      {run.is_active ? (
                        <>
                          <StopCircle className="h-3 w-3" />
                          Close
                        </>
                      ) : (
                        <>
                          <PlayCircle className="h-3 w-3" />
                          Open
                        </>
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {/* Reset Assessment for User Section - HIDDEN */}
        {/* <Card className="p-6 mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Reset Assessment for User</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Select User</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">Choose user...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.profiles?.first_name} {user.profiles?.last_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Select Run</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                value={selectedRunId}
                onChange={(e) => setSelectedRunId(e.target.value)}
              >
                <option value="">Choose run...</option>
                {runs.map((run) => (
                  <option key={run.id} value={run.id}>
                    {run.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="w-full gap-2"
                    disabled={!selectedUserId || !selectedRunId}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reset Assessment
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the user's assessment attempt and all their answers. The user will have to start over from the beginning.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={resetAssessment} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Yes, Reset Assessment
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </Card> */}

        {/* User Management Section */}
        <Card className="p-6 mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4">User Management</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const isUserAdmin = user.user_roles.some((r) => r.role === "admin");
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.profiles?.first_name} {user.profiles?.last_name}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${isUserAdmin ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                        }`}>
                        {isUserAdmin ? "Admin" : "User"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant={isUserAdmin ? "destructive" : "default"}
                        onClick={() => toggleAdminRole(user.id, isUserAdmin)}
                        className="gap-2"
                      >
                        <Shield className="h-3 w-3" />
                        {isUserAdmin ? "Remove Admin" : "Make Admin"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        {/* All Attempts Section */}
        <Card className="p-6">
          <h2 className="text-2xl font-semibold text-foreground mb-4">All User Attempts</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Run</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Completed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attempts.map((attempt) => (
                <TableRow key={attempt.id}>
                  <TableCell>
                    {attempt.profiles?.first_name} {attempt.profiles?.last_name}
                  </TableCell>
                  <TableCell>{attempt.assessment_runs?.name}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${attempt.completed ? "bg-success/20 text-success" : "bg-accent/20 text-accent"
                      }`}>
                      {attempt.completed ? "Completed" : "In Progress"}
                    </span>
                  </TableCell>
                  <TableCell>
                    {attempt.overall_score ? `${Math.round(attempt.overall_score)}%` : "-"}
                  </TableCell>
                  <TableCell>{new Date(attempt.started_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {attempt.completed_at
                      ? new Date(attempt.completed_at).toLocaleDateString()
                      : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
